import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  Wrench,
  CheckCircle,
  AlertTriangle,
  Calendar,
  Play,
  Loader2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter,
  X,
  Cpu,
  ClipboardList,
  ArrowRight,
  CheckCircle2,
  User,
  SlidersHorizontal,
} from "lucide-react";
import { maintenanceAPI } from "../api";
import clsx from "clsx";
import toast from "react-hot-toast";
import { PERMISSIONS } from "../constants/roles";
import HasPermission from "../components/HasPermission";
import { formatIstDate, formatIstDateTime } from "../utils/date";
import ScheduleMaintenance from "../components/ScheduleMaintenance";

// Status configuration matching SOPs & Machines design system
const statusConfig = {
  scheduled: {
    label: "Scheduled",
    badge: "bg-blue-50 text-blue-700 border-blue-200/80",
    dot: "bg-blue-500",
    icon: Calendar,
  },
  in_progress: {
    label: "In Progress",
    badge: "bg-amber-50 text-amber-700 border-amber-200/80",
    dot: "bg-amber-500",
    icon: Play,
  },
  completed: {
    label: "Completed",
    badge: "bg-emerald-50 text-emerald-700 border-emerald-200/80",
    dot: "bg-emerald-500",
    icon: CheckCircle2,
  },
  overdue: {
    label: "Overdue",
    badge: "bg-rose-50 text-rose-700 border-rose-200/80",
    dot: "bg-rose-500",
    icon: AlertTriangle,
  },
  cancelled: {
    label: "Cancelled",
    badge: "bg-slate-100 text-slate-600 border-slate-200/80",
    dot: "bg-slate-400",
    icon: X,
  },
};

const maintenanceTypes = {
  preventive: {
    label: "Preventive",
    badge: "bg-cyan-50 text-cyan-700 border-cyan-200/70",
    dot: "bg-cyan-500",
  },
  corrective: {
    label: "Corrective",
    badge: "bg-orange-50 text-orange-700 border-orange-200/70",
    dot: "bg-orange-500",
  },
  predictive: {
    label: "Predictive",
    badge: "bg-purple-50 text-purple-700 border-purple-200/70",
    dot: "bg-purple-500",
  },
};

/**
 * Metric Summary Card
 */
const SummaryCard = ({ title, value, subtitle, icon: Icon, color = "blue" }) => {
  const colorThemes = {
    blue: {
      bg: "from-blue-50/90 to-white border-blue-100/80 hover:border-blue-200",
      icon: "bg-blue-100/80 text-blue-600",
      badge: "bg-blue-100 text-blue-700",
    },
    amber: {
      bg: "from-amber-50/90 to-white border-amber-100/80 hover:border-amber-200",
      icon: "bg-amber-100/80 text-amber-600",
      badge: "bg-amber-100 text-amber-700",
    },
    rose: {
      bg: "from-rose-50/90 to-white border-rose-100/80 hover:border-rose-200",
      icon: "bg-rose-100/80 text-rose-600",
      badge: "bg-rose-100 text-rose-700",
    },
  };

  const theme = colorThemes[color] || colorThemes.blue;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={clsx(
        "p-4 sm:p-5 bg-gradient-to-br border rounded-2xl shadow-xs hover:shadow-md transition-all duration-200 flex flex-col justify-between relative overflow-hidden group",
        theme.bg
      )}
    >
      <div>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className={clsx("p-2.5 rounded-xl flex-shrink-0", theme.icon)}>
            <Icon className="w-5 h-5" />
          </div>
          {subtitle && (
            <span
              className={clsx(
                "inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full truncate",
                theme.badge
              )}
            >
              {subtitle}
            </span>
          )}
        </div>
        <p className="text-xs sm:text-sm font-medium text-slate-500 mb-1">
          {title}
        </p>
        <p className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {value}
        </p>
      </div>
    </motion.div>
  );
};

/**
 * Maintenance List Item (Refined 12-column table row matching SOPs.jsx)
 */
const MaintenanceListItem = ({ maintenance, index }) => {
  const navigate = useNavigate();
  const status = statusConfig[maintenance.status] || statusConfig.scheduled;
  const mType = maintenanceTypes[maintenance.maintenance_type] || {
    label: maintenance.maintenance_type || "Standard",
    badge: "bg-slate-100 text-slate-700 border-slate-200",
    dot: "bg-slate-400",
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
    >
      <div
        onClick={() => navigate(`/maintenance/${maintenance.id}`)}
        className="grid grid-cols-12 items-center px-4 py-3.5 text-sm border-b border-slate-100/80 transition-colors hover:bg-blue-50/30 group cursor-pointer"
      >
        {/* Col 1: Title, Machine & WO (span 4) */}
        <div className="col-span-4 flex items-center gap-3 min-w-0 pr-3">
          <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 border border-blue-100 flex items-center justify-center flex-shrink-0 group-hover:scale-105 group-hover:bg-blue-600 group-hover:text-white transition-all duration-200">
            <Wrench className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors truncate text-sm">
                {maintenance.title}
              </span>
              {maintenance.work_order_number && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-100 shrink-0">
                  WO: {maintenance.work_order_number}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 truncate">
              <span className="flex items-center gap-1 font-medium text-slate-600 truncate">
                <Cpu className="w-3 h-3 text-slate-400 shrink-0" />
                {maintenance.machine_name || "Unassigned Unit"}
              </span>
              <span className="text-slate-300">•</span>
              <span className={clsx("inline-flex items-center gap-1 text-[11px] font-medium")}>
                <span className={clsx("w-1.5 h-1.5 rounded-full", mType.dot)} />
                {mType.label}
              </span>
            </div>
          </div>
        </div>

        {/* Col 2: Status (span 2) */}
        <div className="col-span-2 flex items-center">
          <span
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border shrink-0",
              status.badge
            )}
          >
            <span className={clsx("w-1.5 h-1.5 rounded-full", status.dot)} />
            <span>{status.label}</span>
          </span>
        </div>

        {/* Col 3: Maintenance Type (span 2) */}
        <div className="col-span-2 flex items-center">
          <span
            className={clsx(
              "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border shrink-0",
              mType.badge
            )}
          >
            <span className={clsx("w-1.5 h-1.5 rounded-full", mType.dot)} />
            <span>{mType.label}</span>
          </span>
        </div>

        {/* Col 4: Assigned Technician (span 2) */}
        <div className="col-span-2 flex items-center gap-2 min-w-0 pr-2">
          <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center shrink-0 text-xs font-semibold">
            {maintenance.technician_name ? maintenance.technician_name.charAt(0).toUpperCase() : "?"}
          </div>
          <span className="text-xs font-medium text-slate-700 truncate" title={maintenance.technician_name || "Unassigned"}>
            {maintenance.technician_name || "Unassigned"}
          </span>
        </div>

        {/* Col 5: Scheduled Date (span 2) */}
        <div className="col-span-2 flex items-center gap-1.5 text-xs text-slate-600">
          <Calendar className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="truncate">
            {maintenance.scheduled_date ? formatIstDateTime(maintenance.scheduled_date) : "Immediate"}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const Maintenance = () => {
  const PAGE_SIZE = 12;
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sort, setSort] = useState("scheduled_desc");

  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  const statusRef = useRef(null);
  const typeRef = useRef(null);
  const previousFilters = useRef({ search, statusFilter, typeFilter, fromDate, toDate, sort });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusRef.current && !statusRef.current.contains(event.target)) setIsStatusOpen(false);
      if (typeRef.current && !typeRef.current.contains(event.target)) setIsTypeOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const filtersChanged = ["search", "statusFilter", "typeFilter", "fromDate", "toDate", "sort"]
      .some((key) => previousFilters.current[key] !== { search, statusFilter, typeFilter, fromDate, toDate, sort }[key]);
    if (filtersChanged) setCurrentPage(1);
    previousFilters.current = { search, statusFilter, typeFilter, fromDate, toDate, sort };
  }, [search, statusFilter, typeFilter, fromDate, toDate, sort]);

  // Primary API Query
  const { data: maintenanceData, isLoading } = useQuery({
    queryKey: [
      "maintenance",
      { search, status: statusFilter, type: typeFilter, fromDate, toDate, sort, page: currentPage },
    ],
    queryFn: () =>
      maintenanceAPI
        .getAll({
          search: search || undefined,
          status: statusFilter || undefined,
          type: typeFilter || undefined,
          from: fromDate || undefined,
          to: toDate || undefined,
          sort,
          page: currentPage,
          limit: PAGE_SIZE,
        })
        .then((res) => res.data),
  });

  const startMutation = useMutation({
    mutationFn: (id) => maintenanceAPI.start(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["maintenance"] });
      toast.success("Ticket claimed and started.");
      navigate(`/maintenance/${id}/execute`);
    },
    onError: (err) => toast.error("Failed to start maintenance: " + (err.response?.data?.error || err.message)),
  });

  const { data: overdueData } = useQuery({
    queryKey: ["maintenance-overdue"],
    queryFn: () => maintenanceAPI.getOverdue().then((res) => res.data?.data || []),
  });

  const rawList = maintenanceData?.data || [];
  const sortedMaintenance = useMemo(() => {
    return [...rawList].sort((a, b) => {
      if (sort === "scheduled_desc") {
        return new Date(b.scheduled_date || 0) - new Date(a.scheduled_date || 0);
      }
      if (sort === "scheduled_asc") {
        return new Date(a.scheduled_date || 0) - new Date(b.scheduled_date || 0);
      }
      if (sort === "created_desc") {
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
      if (sort === "created_asc") {
        return new Date(a.created_at || 0) - new Date(b.created_at || 0);
      }
      return 0;
    });
  }, [rawList, sort]);

  const pagination = maintenanceData?.pagination || {};
  const totalTasks = pagination.total ?? sortedMaintenance.length;
  const totalPages = pagination.pages || Math.ceil(totalTasks / PAGE_SIZE) || 1;
  const inProgressCount = sortedMaintenance.filter((m) => m.status === "in_progress").length;
  const overdueCount = overdueData?.length || 0;

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
    if (currentPage >= totalPages - 3) {
      return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("");
    setTypeFilter("");
    setFromDate("");
    setToDate("");
    setSort("scheduled_desc");
    setCurrentPage(1);
  };

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Top Header matching SOPs & Machines */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            Maintenance Operations
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Preventive schedules, field work orders & equipment service records
          </p>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap self-start sm:self-auto">
          <Link
            to="/work-orders"
            className="btn btn-secondary text-xs sm:text-sm py-2 px-3.5 shadow-xs"
          >
            <ClipboardList className="w-4 h-4 text-slate-500" />
            <span>Work Orders</span>
          </Link>

          <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
            <button
              type="button"
              onClick={() => setIsScheduleModalOpen(true)}
              className="btn btn-primary text-xs sm:text-sm py-2 px-3.5 sm:px-4 shadow-md shadow-blue-500/20 whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span>Schedule Maintenance</span>
            </button>
          </HasPermission>
        </div>
      </div>

      {/* 3 Metric Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
        <SummaryCard
          title="Total Scheduled Tasks"
          value={totalTasks}
          subtitle="All Active Records"
          icon={Wrench}
          color="blue"
        />
        <SummaryCard
          title="In Progress Operations"
          value={inProgressCount}
          subtitle={inProgressCount === 0 ? "Fleet Idle" : "Active Service"}
          icon={Play}
          color="amber"
        />
        <SummaryCard
          title="Overdue Tickets"
          value={overdueCount}
          subtitle={overdueCount === 0 ? "Normal" : "Immediate Action"}
          icon={AlertTriangle}
          color="rose"
        />
      </div>

      {/* Search & Control Filter Bar */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative flex-1 sm:max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search maintenance by task, equipment, or WO#..."
              className="w-full pl-9 pr-9 py-2 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 shadow-xs transition-all"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Filter Dropdowns */}
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
            {/* Status Filter */}
            <div ref={statusRef} className="relative w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsStatusOpen(!isStatusOpen)}
                className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
              >
                <div className="flex items-center gap-2">
                  <Filter className="w-3.5 h-3.5 text-slate-500" />
                  {statusFilter && statusConfig[statusFilter] ? (
                    <div className="flex items-center gap-1.5">
                      <span className={clsx("w-2 h-2 rounded-full", statusConfig[statusFilter].dot)} />
                      <span className="font-semibold text-slate-700">
                        {statusConfig[statusFilter].label}
                      </span>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-700">All Status</span>
                  )}
                </div>
                <ChevronDown className={clsx("w-3.5 h-3.5 text-slate-500 transition-transform duration-200", isStatusOpen && "rotate-180")} />
              </button>
              <AnimatePresence>
                {isStatusOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-1.5 w-full sm:w-52 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20"
                  >
                    <button
                      onClick={() => { setStatusFilter(""); setIsStatusOpen(false); }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                        statusFilter === "" ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span>All Status</span>
                      {statusFilter === "" && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                    </button>
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => { setStatusFilter(key); setIsStatusOpen(false); }}
                        className={clsx(
                          "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                          statusFilter === key ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                          <span>{config.label}</span>
                        </div>
                        {statusFilter === key && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Type Filter */}
            <div ref={typeRef} className="relative w-full sm:w-auto">
              <button
                type="button"
                onClick={() => setIsTypeOpen(!isTypeOpen)}
                className="btn btn-secondary w-full sm:w-auto justify-between h-9 text-xs sm:text-sm px-3.5 bg-white shadow-xs hover:border-slate-400 rounded-xl"
              >
                <div className="flex items-center gap-2">
                  <Wrench className="w-3.5 h-3.5 text-slate-500" />
                  {typeFilter && maintenanceTypes[typeFilter] ? (
                    <div className="flex items-center gap-1.5">
                      <span className={clsx("w-2 h-2 rounded-full", maintenanceTypes[typeFilter].dot)} />
                      <span className="font-semibold text-slate-700">
                        {maintenanceTypes[typeFilter].label}
                      </span>
                    </div>
                  ) : (
                    <span className="font-semibold text-slate-700">All Types</span>
                  )}
                </div>
                <ChevronDown className={clsx("w-3.5 h-3.5 text-slate-500 transition-transform duration-200", isTypeOpen && "rotate-180")} />
              </button>
              <AnimatePresence>
                {isTypeOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-1.5 w-full sm:w-52 origin-top-right rounded-xl bg-white shadow-lg border border-slate-200/90 py-1.5 z-20"
                  >
                    <button
                      onClick={() => { setTypeFilter(""); setIsTypeOpen(false); }}
                      className={clsx(
                        "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                        typeFilter === "" ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
                      )}
                    >
                      <span>All Types</span>
                      {typeFilter === "" && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                    </button>
                    {Object.entries(maintenanceTypes).map(([key, config]) => (
                      <button
                        key={key}
                        onClick={() => { setTypeFilter(key); setIsTypeOpen(false); }}
                        className={clsx(
                          "flex items-center justify-between w-full px-3.5 py-2 text-xs text-left transition-colors font-medium",
                          typeFilter === key ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-700 hover:bg-slate-50"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <span className={clsx("w-2 h-2 rounded-full", config.dot)} />
                          <span>{config.label}</span>
                        </div>
                        {typeFilter === key && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Toggle Advanced Filters (Dates & Sort) */}
            <button
              type="button"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={clsx(
                "btn h-9 text-xs sm:text-sm px-3 rounded-xl transition-all shadow-xs",
                showAdvancedFilters || fromDate || toDate || sort !== "scheduled_desc"
                  ? "bg-blue-50 text-blue-700 border border-blue-200 font-semibold"
                  : "btn-secondary"
              )}
              title="Toggle date range and sorting controls"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Filters</span>
            </button>

            {/* Clear Filters Shortcut */}
            {(search || statusFilter || typeFilter || fromDate || toDate || sort !== "scheduled_desc") && (
              <button
                onClick={resetFilters}
                className="text-xs font-semibold text-slate-500 hover:text-rose-600 px-2.5 py-1.5 rounded-xl hover:bg-rose-50 transition-colors"
                title="Reset all filters"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Collapsible Advanced Filters (Date range and sorting) */}
        <AnimatePresence>
          {showAdvancedFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-2xl flex flex-wrap items-center gap-3.5 text-xs">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-600">From:</span>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className="h-8 px-2.5 bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-xs"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-600">To:</span>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className="h-8 px-2.5 bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-xs"
                  />
                </div>

                <div className="flex items-center gap-2 sm:ml-auto">
                  <span className="font-semibold text-slate-600">Sort By:</span>
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="h-8 px-2.5 bg-white border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 shadow-xs cursor-pointer"
                  >
                    <option value="scheduled_desc">Latest Scheduled</option>
                    <option value="scheduled_asc">Earliest Scheduled</option>
                    <option value="created_desc">Recently Created</option>
                    <option value="created_asc">Oldest Created</option>
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Maintenance Tasks Listing View */}
      {isLoading ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="divide-y divide-slate-100">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-12 items-center p-4 border-b border-slate-100 last:border-b-0 animate-pulse"
              >
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-200 flex-shrink-0" />
                  <div className="space-y-1.5 flex-1">
                    <div className="h-4 w-44 bg-slate-200 rounded" />
                    <div className="h-3 w-24 bg-slate-100 rounded" />
                  </div>
                </div>
                <div className="col-span-2">
                  <div className="h-6 w-24 bg-slate-200 rounded-full" />
                </div>
                <div className="col-span-2">
                  <div className="h-5 w-20 bg-slate-100 rounded-lg" />
                </div>
                <div className="col-span-2">
                  <div className="h-5 w-24 bg-slate-100 rounded-lg" />
                </div>
                <div className="col-span-2">
                  <div className="h-4 w-28 bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : sortedMaintenance && sortedMaintenance.length > 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-[760px]">
              {/* Table Header (4-2-2-2-2 = 12 cols, matching SOPs without action col) */}
              <div className="grid grid-cols-12 items-center text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50/80 border-b border-slate-200/80 px-4 py-3">
                <div className="col-span-4">Task / Machine</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2">Assigned To</div>
                <div className="col-span-2">Scheduled Time</div>
              </div>

              {/* List Body */}
              <div className="divide-y divide-slate-100/90">
                <AnimatePresence>
                  {sortedMaintenance.map((item, index) => (
                    <MaintenanceListItem
                      key={item.id}
                      maintenance={item}
                      index={index}
                    />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Pagination Controls */}
          {totalTasks > PAGE_SIZE && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3.5 border-t border-slate-200/80">
              <p className="text-xs text-slate-500 font-medium">
                Showing{" "}
                <span className="font-semibold text-slate-700">
                  {(currentPage - 1) * PAGE_SIZE + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold text-slate-700">
                  {Math.min(currentPage * PAGE_SIZE, totalTasks)}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-700">{totalTasks}</span>{" "}
                maintenance tasks
              </p>

              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage <= 1
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Prev</span>
                </button>

                <div className="flex items-center gap-1">
                  {getPageNumbers().map((pageItem, idx) =>
                    pageItem === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="w-8 h-8 flex items-center justify-center text-xs text-slate-400 font-bold"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageItem}
                        type="button"
                        onClick={() => setCurrentPage(pageItem)}
                        className={clsx(
                          "w-8 h-8 rounded-xl text-xs font-semibold transition-all",
                          currentPage === pageItem
                            ? "bg-blue-600 text-white shadow-sm shadow-blue-500/30 font-bold"
                            : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 shadow-xs"
                        )}
                      >
                        {pageItem}
                      </button>
                    )
                  )}
                </div>

                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  className={clsx(
                    "h-8 px-2.5 rounded-xl border text-xs font-medium flex items-center gap-1 transition-colors",
                    currentPage >= totalPages
                      ? "border-slate-200 text-slate-300 cursor-not-allowed bg-slate-50"
                      : "border-slate-200 text-slate-700 bg-white hover:bg-slate-50 active:bg-slate-100 shadow-xs"
                  )}
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Empty State */
        <div className="p-10 sm:p-14 bg-white border border-dashed border-slate-300 rounded-2xl text-center flex flex-col items-center justify-center">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3">
            <Wrench className="w-7 h-7" />
          </div>
          <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
            No Maintenance Tasks Found
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 max-w-sm mb-5">
            {search || statusFilter || typeFilter || fromDate || toDate
              ? "No maintenance tasks matched your search or filter criteria. Try clearing the filters."
              : "No upcoming or in-progress maintenance activities are currently scheduled."}
          </p>
          {search || statusFilter || typeFilter || fromDate || toDate ? (
            <button
              onClick={resetFilters}
              className="btn btn-secondary text-xs sm:text-sm py-2 px-4"
            >
              Reset Filters
            </button>
          ) : (
            <HasPermission allowedRoles={PERMISSIONS.MANAGE_MAINTENANCE}>
              <button
                type="button"
                onClick={() => setIsScheduleModalOpen(true)}
                className="btn btn-primary inline-flex text-xs sm:text-sm py-2 px-4 shadow-md shadow-blue-500/20"
              >
                <Plus className="w-4 h-4" />
                Schedule Maintenance
              </button>
            </HasPermission>
          )}
        </div>
      )}

      {/* Schedule Maintenance Popup Modal */}
      <ScheduleMaintenance
        isOpen={isScheduleModalOpen}
        onClose={() => setIsScheduleModalOpen(false)}
      />
    </div>
  );
};

export default Maintenance;